import { writable, derived } from 'svelte/store';
import type { Notification } from '$lib/types';

interface NotificationStore {
  notifications: Notification[];
}

function createNotificationStore() {
  const { subscribe, update } = writable<NotificationStore>({
    notifications: []
  });

  let notificationId = 0;

  function add(type: Notification['type'], message: string, duration: number = 5000): string {
    const id = `notification-${++notificationId}`;

    update((state) => ({
      notifications: [...state.notifications, { id, type, message, duration }]
    }));

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => remove(id), duration);
    }

    return id;
  }

  function remove(id: string) {
    update((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id)
    }));
  }

  function clear() {
    update(() => ({ notifications: [] }));
  }

  return {
    subscribe,
    info: (message: string, duration?: number) => add('info', message, duration),
    success: (message: string, duration?: number) => add('success', message, duration),
    warning: (message: string, duration?: number) => add('warning', message, duration),
    error: (message: string, duration?: number) => add('error', message, duration),
    remove,
    clear
  };
}

export const notifications = createNotificationStore();

// Derived store for easy access to just the notifications array
export const notificationList = derived(
  notifications,
  ($notifications) => $notifications.notifications
);
